import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Wat is een consultancy contract?',
    answer: 'Een consultancy contract is een overeenkomst tussen een zelfstandige consultant (freelancer) en een opdrachtgever voor het leveren van advies- of consultancydiensten. Het legt de scope van de opdracht, het honorarium, de looptijd en de verantwoordelijkheden van beide partijen vast.',
  },
  {
    question: 'Wat moet er in een consultancy contract staan?',
    answer: 'Een volledig consultancy contract bevat: een duidelijke omschrijving van de consultancydiensten, het afgesproken tarief (uurtarief of projectbasis), de looptijd en opzeggingsvoorwaarden, afspraken over vertrouwelijkheid, intellectueel eigendom, aansprakelijkheidsbeperking en de toepasselijke wetgeving.',
  },
  {
    question: 'Verschil tussen freelance en consultancy contract?',
    answer: 'Een freelance contract is doorgaans gericht op het uitvoeren van concrete taken of het opleveren van specifieke producten (bijv. een website bouwen). Een consultancy contract richt zich op het leveren van expertise en advies. Consultancy contracten bevatten vaak meer nadruk op vertrouwelijkheid, intellectueel eigendom van adviezen en aansprakelijkheid voor gegeven adviezen.',
  },
  {
    question: 'Is een consultancy contract verplicht?',
    answer: 'Een schriftelijk consultancy contract is niet wettelijk verplicht, maar het is sterk aanbevolen voor elke zelfstandige consultant. Het beschermt je bij geschillen, legt de scope van de opdracht vast en voorkomt misverstanden over betaling en verantwoordelijkheden. Veel grote bedrijven eisen een ondertekend contract voordat ze met een consultant samenwerken.',
  },
  {
    question: 'Kunnen consultants een template gebruiken?',
    answer: 'Ja, met Pactorae kunnen zelfstandige consultants gratis een professioneel consultancy contract genereren op maat. Onze generator stelt de juiste vragen over jouw consultancydiensten en maakt in 30 seconden een juridisch correct contract dat direct bruikbaar is in België en Nederland.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/consultancy-contract-template',
      url: 'https://pactorae.com/consultancy-contract-template',
      name: 'Consultancy Contract Template voor Freelancers | Pactorae',
      description:
        'Gratis consultancy contract template voor freelancers en zelfstandige consultants in België en Nederland. Professioneel, juridisch correct en direct inzetbaar.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-[#2F4E6F] pr-4">{faq?.question}</span>
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
          style={{ backgroundColor: '#FF8C00', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
        </div>
      )}
    </div>
  );
};

const ConsultancyPreview = () => (
  <div className="relative">
    <div
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      style={{ maxWidth: '720px', margin: '0 auto' }}
    >
      <div style={{ padding: '40px 48px 0 48px', fontFamily: 'Georgia, serif', color: '#1a1a1a', lineHeight: '1.8', fontSize: '14px' }}>
        <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.08em', marginBottom: '4px', textTransform: 'uppercase' }}>CONSULTANCY OVEREENKOMST</p>
        <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', marginBottom: '32px' }}>Opgesteld via Pactorae — pactorae.com</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>PARTIJEN</p>
        <p style={{ marginBottom: '4px' }}><strong>Consultant:</strong> Sarah De Backer, Keizerslei 55, 2000 Antwerpen, BTW BE0567.890.234</p>
        <p style={{ marginBottom: '24px' }}><strong>Opdrachtgever:</strong> FinanceGroup NV, Regentlaan 40, 1000 Brussel, BTW BE0432.109.876</p>

        <p style={{ marginBottom: '16px' }}>Hierna gezamenlijk aangeduid als "de Partijen", komen het volgende overeen:</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 1 — CONSULTANCYDIENSTEN</p>
        <p style={{ marginBottom: '8px' }}>Consultant verbindt zich ertoe de volgende consultancydiensten te leveren aan Opdrachtgever:</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Strategische analyse van de huidige financiële processen en systemen</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Opstellen van een implementatieplan voor digitale transformatie</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Begeleiding bij de selectie van een nieuw ERP-systeem</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Maandelijkse voortgangsrapportages en presentaties aan het management</p>
        <p style={{ marginBottom: '24px', paddingLeft: '16px' }}>• Eindrapport met aanbevelingen en implementatieplan</p>

        <p style={{ marginBottom: '16px' }}>De consultancydiensten worden uitgevoerd in de periode van 1 april 2026 tot en met 30 september 2026, met een verwachte inzet van gemiddeld 2 dagen per week.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 2 — HONORARIUM EN BETALINGSVOORWAARDEN</p>
        <p style={{ marginBottom: '8px' }}>Opdrachtgever betaalt Consultant een honorarium van <strong>€ 1.200,00 (twaalfhonderd euro) per dag</strong>, exclusief BTW (21%).</p>
        <p style={{ marginBottom: '16px' }}>Facturatie geschiedt maandelijks op basis van de werkelijk gepresteerde dagen. Betaaltermijn: <strong>30 dagen</strong> na factuurdatum.</p>
        <p style={{ marginBottom: '24px' }}>Reiskosten en verblijfskosten worden apart gefactureerd op basis van werkelijke kosten, mits vooraf goedgekeurd door Opdrachtgever.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 3 — LOOPTIJD EN OPZEGGING</p>
        <p style={{ marginBottom: '16px' }}>Deze overeenkomst treedt in werking op 1 april 2026 en eindigt op 30 september 2026. Tussentijdse opzegging is mogelijk met inachtneming van een opzegtermijn van <strong>30 kalenderdagen</strong>.</p>
        <p style={{ marginBottom: '24px' }}>Bij opzegging door Opdrachtgever zijn alle reeds verrichte consultancydiensten verschuldigd op basis van het overeengekomen dagtarief.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 4 — VERTROUWELIJKHEID</p>
        <p style={{ marginBottom: '16px' }}>Consultant verbindt zich ertoe alle vertrouwelijke informatie van Opdrachtgever strikt geheim te houden, zowel tijdens als gedurende <strong>5 jaar</strong> na beëindiging van deze overeenkomst. Consultant zal geen gebruik maken van de vertrouwelijke informatie voor eigen doeleinden of ten behoeve van derden.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 5 — INTELLECTUEEL EIGENDOM</p>
        <p style={{ marginBottom: '16px' }}>Alle rapporten, analyses, presentaties en overige documenten die Consultant in het kader van deze overeenkomst opstelt, worden na volledige betaling eigendom van Opdrachtgever. Consultant behoudt het recht om algemene methodologieën en niet-vertrouwelijke kennis te gebruiken in toekomstige opdrachten.</p>
      </div>

      {/* Fade-out gradient overlay */}
      <div
        style={{
          height: '160px',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,1) 100%)',
          marginTop: '-80px',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Below fade message */}
      <div style={{ padding: '0 48px 32px 48px', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', marginBottom: '20px' }}>
          Volledig contract genereren met de Pactorae generator
        </p>
        <Link
          to="/contract-generator"
          className="inline-block font-semibold px-8 py-3 rounded-lg text-white transition-colors duration-200 shadow-md hover:shadow-lg"
          style={{ backgroundColor: '#FF8C00' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e07b00'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FF8C00'}
        >
          Genereer jouw contract gratis
        </Link>
      </div>
    </div>
  </div>
);

const ConsultancyContractTemplatePage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>Consultancy Contract Template België &amp; Nederland | Pactorae</title>
        <meta
          name="description"
          content="Gratis consultancy contract template voor freelancers en zelfstandige consultants in België en Nederland. Professioneel, juridisch correct voor zzp'ers en zelfstandigen. Genereer direct online."
        />
        <meta
          name="keywords"
          content="consultancy contract België, consultancy contract Nederland, consulting agreement zzp, consultancy overeenkomst zelfstandige, freelance consultancy België, freelance consultancy Nederland"
        />
        <link rel="canonical" href="https://pactorae.com/consultancy-contract-template" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/consultancy-contract-template" />
        <meta property="og:title" content="Consultancy Contract Template België &amp; Nederland | Pactorae" />
        <meta
          property="og:description"
          content="Gratis consultancy contract template voor freelancers en zelfstandige consultants in België en Nederland. Professioneel en juridisch correct."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Consultancy Contract Template België &amp; Nederland | Pactorae" />
        <meta
          name="twitter:description"
          content="Gratis consultancy contract template voor freelancers en zzp'ers in België en Nederland."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#FF8C00]/20 text-[#FF8C00] text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-[#FF8C00]/30">
              Gratis Template
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Consultancy Contract Template
              <span className="block text-[#FF8C00] mt-2">België &amp; Nederland</span>
            </h1>
            <p className="text-xl text-blue-100 mb-4 max-w-2xl mx-auto">
              Professioneel consultancy contract voor zelfstandige consultants in België en Nederland. Juridisch correct, volledig op maat en gratis te genereren.
            </p>
            <p className="text-sm text-blue-200 mb-8 max-w-xl mx-auto">
              Geschikt voor zelfstandigen in België, zzp'ers in Nederland en alle freelance consultants.
            </p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Wat is een consultancy contract voor België en Nederland?</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Een consultancy contract is een overeenkomst tussen een zelfstandige consultant en een opdrachtgever voor het leveren van advies- en consultancydiensten. Of je nu als zelfstandige in België werkt of als zzp'er in Nederland — een goed consultancy contract beschermt jou én jouw opdrachtgever. Onze generator maakt contracten op maat voor zowel het Belgisch als het Nederlands recht.
            </p>
          </section>

          {/* What's included */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Wat bevat dit consultancy contract template?</h2>
            <ul className="space-y-3">
              {[
                { title: 'Scope van de consultancydiensten', desc: 'Exacte omschrijving van de te leveren adviesdiensten' },
                { title: 'Honorarium en betalingsvoorwaarden', desc: 'Uurtarief of projectbasis, facturatie en betalingstermijnen' },
                { title: 'Looptijd en opzegging', desc: 'Duur van de opdracht en opzeggingsvoorwaarden' },
                { title: 'Vertrouwelijkheid', desc: 'Geheimhouding van bedrijfsinformatie en adviezen' },
                { title: 'Intellectueel eigendom', desc: 'Eigendom van rapporten, analyses en aanbevelingen' },
                { title: 'Aansprakelijkheidsbeperking', desc: 'Beperking van aansprakelijkheid voor gegeven adviezen' },
                { title: 'Onafhankelijkheid', desc: 'Bevestiging van de zelfstandige status van de consultant' },
                { title: 'Toepasselijk recht', desc: 'Belgisch of Nederlands recht en bevoegde rechtbank' },
              ]?.map((item, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <span className="font-semibold text-[#2F4E6F]">{item?.title}:</span>{' '}
                    <span className="text-gray-600">{item?.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Consultancy Preview */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Voorbeeld consultancy contract</h2>
            <ConsultancyPreview />
          </section>

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-xl font-bold text-[#2F4E6F] mb-4">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { to: '/freelance-contract-template', label: 'Freelance Contract Template' },
                { to: '/nda-template', label: 'NDA Template voor Freelancers' },
                { to: '/algemene-voorwaarden-generator', label: 'Algemene Voorwaarden Generator' },
                { to: '/freelance-contract-voorbeeld', label: 'Freelance Contract Voorbeeld' },
              ]?.map((link) => (
                <Link
                  key={link?.to}
                  to={link?.to}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors text-[#2F4E6F] font-medium text-sm"
                >
                  <span style={{ color: '#FF8C00' }}>→</span> {link?.label}
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-8">Veelgestelde vragen over consultancy contracten</h2>
            <div className="space-y-3">
              {faqs?.map((faq, i) => (
                <FaqItem key={i} faq={faq} />
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl p-12">
            <h2 className="text-2xl font-bold text-white mb-4">Klaar om jouw consultancy contract te genereren?</h2>
            <p className="text-blue-100 mb-8">Maak in 30 seconden een professioneel consultancy contract op maat.</p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </section>
        </article>
      </main>
    </>
  );
};

export default ConsultancyContractTemplatePage;
